// Pure helpers shared by the Templates list, the draft editor, and the preview.
// The editor state shape is:
//   { uuid?, key, title, description, subjectScope, subjects: [uuid], questions: [
//       { uuid?, key, text, isRequired, answerType, optionSet,
//         conditions: [{ dependsOnKey, operator, expectedOption }] } ] }
// `key` is a payload-local handle (existing rows reuse their uuid) so
// visibility conditions can reference questions that have no uuid yet.
// Conditions are a full graph: any question may depend on any other (ANDed,
// equals / not_equals), mirroring the backend's QuestionVisibilityCondition.
//
// EVERY authored string above — `title`, `description`, `text` — is a
// per-language OBJECT `{kk, ru, en}`, never a bare string. The editor exists to
// show each language column as it actually is, and a bare string can only hold
// one of them; it is also how the old code destroyed translations, by copying
// whichever single string it held into all three columns on save.
import { LANGUAGE_IDS, CONTENT_FALLBACK } from "../../lang/languages";
import { scopeCovers } from "../../utils/helpers/standardScope";

/** An empty value for one translated field: `{kk: "", ru: "", en: ""}`. */
export const emptyText = () =>
  Object.fromEntries(LANGUAGE_IDS.map((id) => [id, ""]));

/** Read one field's language columns off an API row. */
export const textFromApi = (row, field) => {
  const columns = row?.translations?.[field];
  if (columns) {
    return Object.fromEntries(
      LANGUAGE_IDS.map((id) => [id, columns[id] || ""]),
    );
  }
  // No `translations` block — the row came from a request that did not ask for
  // one. Defensive only (the editor always passes `?translations=1`): the flat
  // value is the active language's, and before per-language authoring existed
  // every column held the same string anyway, so mirroring it is the closest
  // truthful reconstruction.
  const flat = row?.[field] || "";
  return Object.fromEntries(LANGUAGE_IDS.map((id) => [id, flat]));
};

/** The string to SHOW for a translated value, walking the fallback chain. */
export const displayText = (value, lang) => {
  for (const id of CONTENT_FALLBACK[lang] || LANGUAGE_IDS) {
    if ((value?.[id] || "").trim()) return value[id];
  }
  return "";
};

/** Does this translated value carry text in at least one language? */
export const hasText = (value) =>
  LANGUAGE_IDS.some((id) => (value?.[id] || "").trim());

export const trimText = (value) =>
  Object.fromEntries(LANGUAGE_IDS.map((id) => [id, (value?.[id] || "").trim()]));

/** Do two translated values hold the same text in every language? */
export const sameText = (a, b) =>
  LANGUAGE_IDS.every((id) => (a?.[id] || "") === (b?.[id] || ""));

/**
 * Per-language authoring progress, as `{kk: {done, total}, …}`.
 *
 * Counts only the strings a template cannot be published without — the
 * template name, every standard title and every question text. Optional
 * descriptions are excluded deliberately: including them would park the
 * counter permanently below 100% on a perfectly finished template, which
 * trains the author to ignore it.
 */
export const translationCoverage = (standards, templateName) => {
  const values = [templateName, ...standards.flatMap((std) => [
    std.title,
    ...std.questions.map((q) => q.text),
  ])].filter(Boolean);
  return Object.fromEntries(
    LANGUAGE_IDS.map((id) => [
      id,
      {
        done: values.filter((v) => (v?.[id] || "").trim()).length,
        total: values.length,
      },
    ]),
  );
};

let newKeyCounter = 0;
export const nextQuestionKey = () => `new-${++newKeyCounter}`;

export const findDefaultOptionSet = (optionSets = []) =>
  optionSets.find((s) => s.code === "yes_no_na") || optionSets[0] || null;

export const positiveOptionOf = (optionSet) =>
  optionSet?.options?.find((o) => o.is_positive) || optionSet?.options?.[0] || null;

export const optionSetsByUuid = (optionSets = []) =>
  Object.fromEntries(optionSets.map((s) => [s.uuid, s]));

export const apiTreeToEditorState = (version) => {
  const knownUuids = new Set(
    (version?.standards || []).flatMap((std) =>
      (std.questions || []).map((q) => q.uuid),
    ),
  );
  return (version?.standards || []).map((std) => {
    const questions = (std.questions || []).filter((q) => !q.parent_question);
    return {
      uuid: std.uuid,
      key: std.uuid,
      title: textFromApi(std, "title"),
      description: textFromApi(std, "description"),
      subjectScope: std.subject_scope || "all",
      subjects: (std.subjects || []).map((s) => s.uuid),
      questions: questions.map((q) => ({
        uuid: q.uuid,
        key: q.uuid,
        text: textFromApi(q, "text"),
        isRequired: q.is_required !== false,
        answerType: q.answer_type || "single_choice",
        optionSet: q.answer_option_set || null,
        conditions: (q.visibility_conditions || [])
          .filter((c) => knownUuids.has(c.depends_on_question))
          .map((c) => ({
            dependsOnKey: c.depends_on_question,
            operator: c.operator || "equals",
            expectedOption: c.expected_option,
          })),
      })),
    };
  });
};

export const emptyQuestion = () => ({
  uuid: null,
  key: nextQuestionKey(),
  text: emptyText(),
  isRequired: true,
  answerType: "single_choice",
  optionSet: null,
  conditions: [],
});

export const emptyStandard = () => ({
  uuid: null,
  key: nextQuestionKey(),
  title: emptyText(),
  description: emptyText(),
  subjectScope: "all",
  subjects: [],
  questions: [emptyQuestion()],
});

// -> PUT /template-versions/{uuid}/content/ body. Conditions referencing
// questions no longer in the tree are dropped defensively (the editor also
// cleans them up on delete).
export const buildContentPayload = (standards, optionSets) => {
  const defaultSet = findDefaultOptionSet(optionSets);
  const keys = new Set(standards.flatMap((std) => std.questions.map((q) => q.key)));
  return standards.map((std) => ({
    ...(std.uuid ? { uuid: std.uuid } : {}),
    title: trimText(std.title),
    description: trimText(std.description),
    ...scopePayload(std),
    questions: std.questions.map((q) => ({
      ...(q.uuid ? { uuid: q.uuid } : {}),
      key: q.key,
      text: trimText(q.text),
      is_required: q.isRequired,
      answer_type: q.answerType || "single_choice",
      answer_option_set: q.optionSet || defaultSet?.uuid || null,
      visibility_conditions: (q.conditions || [])
        .filter((c) => c.dependsOnKey !== q.key && keys.has(c.dependsOnKey) && c.expectedOption)
        .map((c) => ({
          depends_on_key: c.dependsOnKey,
          operator: c.operator || "equals",
          expected_option: c.expectedOption,
        })),
    })),
  }));
};

// The condition picker only offers earlier questions, but reordering after
// authoring can still close a loop — catch it before the backend does.
// Returns the keys of the questions on the first cycle found, or null.
// Dangling dependsOnKey refs are skipped on purpose: the editor strips them on
// delete and buildContentPayload filters them, so they can't form a cycle.
export const findConditionCycle = (standards) => {
  const graph = {};
  standards.forEach((std) =>
    std.questions.forEach((q) => {
      graph[q.key] = (q.conditions || []).map((c) => c.dependsOnKey);
    }),
  );
  const WHITE = 0, GRAY = 1, BLACK = 2;
  const color = Object.fromEntries(Object.keys(graph).map((k) => [k, WHITE]));
  const stack = [];
  let cycle = null;
  const visit = (node) => {
    color[node] = GRAY;
    stack.push(node);
    for (const dep of graph[node] || []) {
      if (!(dep in color)) continue;
      if (color[dep] === GRAY) {
        cycle = stack.slice(stack.indexOf(dep));
        return true;
      }
      if (color[dep] === WHITE && visit(dep)) return true;
    }
    color[node] = BLACK;
    stack.pop();
    return false;
  };
  Object.keys(graph).some((k) => color[k] === WHITE && visit(k));
  return cycle;
};

// A standard's scope as the API takes it. Switching to "all" keeps the list in
// the editor so switching back restores it; the server only ever stores it
// under a scope that reads it.
export const scopePayload = (std) => ({
  subject_scope: std.subjectScope,
  subjects: std.subjectScope === "all" ? [] : std.subjects,
});

// Does `std` take other lessons than `stored`, its scope as saved? A list kept
// under "all" (so flipping back restores it) is not a change.
export const isScopeChanged = (std, stored) => {
  const next = scopePayload(std);
  const before = scopePayload(stored);
  return (
    next.subject_scope !== before.subject_scope ||
    next.subjects.length !== before.subjects.length ||
    next.subjects.some((uuid) => !before.subjects.includes(uuid))
  );
};

export const isScopeMissingSubjects = (std) =>
  std.subjectScope !== "all" && !std.subjects.length;

// Returns an i18n key for the first subject-scope problem, or null. A question
// gated by another standard's question is shown only where both standards are
// scored, so the gate's standard must reach every lesson the gated one does —
// the same rule the server enforces.
export const findScopeProblem = (standards) => {
  if (standards.some(isScopeMissingSubjects)) return "templates.subjectsRequired";
  const scopeOfQuestion = {};
  standards.forEach((std) =>
    std.questions.forEach((q) => {
      scopeOfQuestion[q.key] = [std.subjectScope, std.subjects];
    }),
  );
  const unreachable = standards.some((std) =>
    std.questions.some((q) =>
      (q.conditions || []).some(
        (c) =>
          scopeOfQuestion[c.dependsOnKey] &&
          !scopeCovers(scopeOfQuestion[c.dependsOnKey], [std.subjectScope, std.subjects]),
      ),
    ),
  );
  return unreachable ? "templates.gateOutOfScope" : null;
};

// Returns an i18n key for the first problem, or null when publishable.
export const validateEditorState = (standards) => {
  if (!standards.length) return "templates.needStandard";
  for (const std of standards) {
    // ONE language is enough. The backend falls back kk -> ru -> en, so a
    // partly translated template renders everywhere; demanding all three would
    // stop a school that has only ever needed Kazakh from publishing at all.
    if (!hasText(std.title)) return "templates.standardTitleRequired";
    if (!std.questions.length) return "templates.needQuestion";
    for (const q of std.questions) {
      if (!hasText(q.text)) return "templates.questionTextRequired";
    }
  }
  if (findConditionCycle(standards)) return "templates.conditionCycle";
  return findScopeProblem(standards);
};

export const countConditions = (standards) =>
  standards.reduce(
    (total, std) =>
      total + std.questions.reduce((n, q) => n + (q.conditions || []).length, 0),
    0,
  );

// Visual nesting depth per question within a standard: one level deeper than
// the deepest same-standard question it depends on (cross-standard gates
// still indent one level — there is no parent row to nest under). Capped so
// long chains stay readable. Document order is unchanged — this only drives
// indentation.
export const computeQuestionDepths = (std) => {
  const keys = new Set(std.questions.map((q) => q.key));
  const depths = {};
  std.questions.forEach((q) => {
    const conds = q.conditions || [];
    if (!conds.length) {
      depths[q.key] = 0;
      return;
    }
    const parentDepths = conds
      .filter((c) => keys.has(c.dependsOnKey))
      .map((c) => depths[c.dependsOnKey])
      .filter((d) => d !== undefined);
    depths[q.key] = Math.min(
      1 + (parentDepths.length ? Math.max(...parentDepths) : 0),
      3,
    );
  });
  return depths;
};

// Which questions are visible given the current answers ({key: optionUuid})?
// Fixpoint evaluation matching the live form and the backend: a condition
// passes only when its gate is answered with (equals) / without (not_equals)
// the expected option; answers of hidden questions are pruned and visibility
// re-evaluated until stable, so chains collapse correctly.
export const computeVisibleKeys = (standards, answers) => {
  const questions = standards.flatMap((std) => std.questions);
  const evaluate = (current) =>
    new Set(
      questions
        .filter((q) =>
          (q.conditions || []).every((c) => {
            const selected = current[c.dependsOnKey];
            if (!selected) return false;
            return c.operator === "not_equals"
              ? selected !== c.expectedOption
              : selected === c.expectedOption;
          }),
        )
        .map((q) => q.key),
    );
  const prune = (current, visible) =>
    Object.fromEntries(Object.entries(current).filter(([key]) => visible.has(key)));

  let current = { ...answers };
  let visible = evaluate(current);
  let pruned = prune(current, visible);
  while (Object.keys(pruned).length !== Object.keys(current).length) {
    current = pruned;
    visible = evaluate(current);
    pruned = prune(current, visible);
  }
  return visible;
};

export const arrayMove = (list, from, to) => {
  if (to < 0 || to >= list.length) return list;
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
};
